import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// ============ helpers ============

async function getMyTc(_supabase: any, userId: string) {
  // pix_key/pix_beneficiary are sensitive; read via admin client scoped by owner
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("training_centers")
    .select("id, pix_key, pix_beneficiary, city")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Cadastre seu Centro de Treinamento.");
  return data as { id: string; pix_key: string | null; pix_beneficiary: string | null; city: string | null };
}

function onlyDigits(s: string | null | undefined) {
  return (s || "").replace(/\D/g, "");
}

function trimSlash(s: string) {
  return (s || "").replace(/\/+$/, "");
}

function moneyBR(n: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);
}

async function sendViaEvolution(tcId: string, supabase: any, phone: string, text: string) {
  const { data: cfg } = await supabase
    .from("whatsapp_configs")
    .select("*")
    .eq("training_center_id", tcId)
    .maybeSingle();
  if (!cfg?.base_url || !cfg?.global_api_key || !cfg?.instance_name) {
    throw new Error("Configure a integração WhatsApp (Evolution) primeiro.");
  }
  const digits = onlyDigits(phone);
  if (!digits) throw new Error("Telefone do responsável inválido.");
  const number = digits.length <= 11 ? `55${digits}` : digits;
  const url = `${trimSlash(cfg.base_url)}/message/sendText/${cfg.instance_name}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: cfg.global_api_key },
    body: JSON.stringify({ number, text }),
  });
  const body = await res.text();
  if (!res.ok) throw new Error(`WhatsApp falhou (${res.status}): ${body.slice(0, 200)}`);
  await supabase.from("whatsapp_message_logs").insert({
    training_center_id: tcId,
    recipient_phone: number,
    recipient_name: "Responsável",
    message: text,
    status: "sent",
  });
  return { ok: true };
}

// ============ Send PIX message on sale ============

export const sendPixSaleWhatsapp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      kind: z.enum(["uniform", "product"]),
      order_id: z.string().uuid(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    if (!tc.pix_key || !tc.pix_beneficiary) {
      throw new Error("Cadastre a Chave PIX e o Nome do Beneficiário em Dados do CT.");
    }
    const table = data.kind === "uniform" ? "uniform_orders" : "product_orders";
    const itemFk = data.kind === "uniform" ? "uniforms(name)" : "products(name)";
    const { data: row, error } = await context.supabase
      .from(table)
      .select(`id, amount, discount, training_center_id, students(id, name, parent:parents(name, phone)), ${itemFk}`)
      .eq("id", data.order_id).single();
    if (error) throw error;
    if (row.training_center_id !== tc.id) throw new Error("Sem permissão.");
    const parent = (row as any).students?.parent;
    if (!parent?.phone) throw new Error("Responsável do aluno sem WhatsApp cadastrado.");
    const itemName = (row as any)[data.kind === "uniform" ? "uniforms" : "products"]?.name ?? "Compra";
    const alunoName = (row as any).students?.name ?? "";
    const net = Math.max(0, Number(row.amount) - Number(row.discount ?? 0));

    const { buildPixBrCode } = await import("@/lib/pix/brcode");
    const brcode = buildPixBrCode({
      pixKey: tc.pix_key!,
      beneficiaryName: tc.pix_beneficiary || "PAGADOR",
      city: (tc as any).city || "BRASIL",
      amount: net > 0 ? net : undefined,
      txid: String(row.id).replace(/-/g, "").slice(0, 25),
      description: itemName,
    });

    const messages = [
`Olá! 👋 Compra registrada: *${itemName}*${alunoName ? ` (aluno: ${alunoName})` : ""}.

💰 Valor a pagar: *${moneyBR(net)}*

A próxima mensagem traz o *Pix copia-e-cola* já com o valor incluso. Toque e segure para copiar e cole no app do banco.`,
`📎 *Após pagar, ENVIE O COMPROVANTE neste mesmo número.*
Favorecido: *${tc.pix_beneficiary}*
Pix *copia-e-cola* (valor já incluso) na próxima mensagem — toque e segure para copiar.`,
      brcode,
    ];

    for (const m of messages) {
      await sendViaEvolution(tc.id, context.supabase, parent.phone, m);
    }
    return { ok: true };

  });

// ============ Listing ============

export const listPixProofs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      status: z.enum(["aguardando", "confirmado", "rejeitado", "all"]).default("aguardando"),
      date_from: z.string().nullable().optional(), // YYYY-MM-DD
      date_to: z.string().nullable().optional(),   // YYYY-MM-DD
      month: z.string().nullable().optional(),     // YYYY-MM
      search: z.string().nullable().optional(),    // student/payer name or value
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    let q = context.supabase
      .from("pix_payment_proofs")
      .select("*")
      .eq("training_center_id", tc.id)
      // Cobranças de contrato (source='contract') e de taxa (source='fee')
      // aparecem em suas próprias telas / fluxos — aqui listamos apenas
      // comprovantes efetivamente recebidos (manual/whatsapp).
      .not("source", "in", "(contract,fee)")
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.status !== "all") q = q.eq("status", data.status);
    // Descartados: só exibe rejeições manuais — auto-descarte da IA fica oculto.
    if (data.status === "rejeitado") q = q.eq("auto_discarded", false);

    // Date filters apply to pix_date (preferred) — fallback to created_at when null.
    if (data.month && /^\d{4}-\d{2}$/.test(data.month)) {
      const [y, m] = data.month.split("-").map(Number);
      const start = `${data.month}-01`;
      const endDate = new Date(Date.UTC(y, m, 0)); // last day of month
      const end = `${data.month}-${String(endDate.getUTCDate()).padStart(2, "0")}`;
      q = q.gte("pix_date", start).lte("pix_date", end);
    } else {
      if (data.date_from) q = q.gte("pix_date", data.date_from);
      if (data.date_to) q = q.lte("pix_date", data.date_to);
    }

    // Search: payer name OR numeric amount
    const search = (data.search ?? "").trim();
    if (search) {
      const num = Number(search.replace(/\./g, "").replace(",", "."));
      if (Number.isFinite(num) && num > 0) {
        q = q.eq("amount", num);
      } else {
        q = q.ilike("payer_name", `%${search}%`);
      }
    }

    const { data: rows, error } = await q;
    if (error) throw error;

    // Re-rodar match para "aguardando" sem match (novos candidatos podem ter surgido depois).
    if (data.status === "aguardando" && (rows?.length ?? 0) > 0) {
      const { tryAutoMatch, applyConfirmation } = await import("@/lib/pix-proofs/processor.server");
      for (const r of rows!) {
        if (r.match_level && r.match_level !== "none") continue;
        if (r.amount == null) continue;
        // Só re-processa comprovantes reais (com imagem) — nunca cobranças/placeholders.
        if (!r.image_url) continue;
        const m = await tryAutoMatch(context.supabase, tc.id, {
          payer_name: r.payer_name,
          payer_cpf_middle: r.payer_cpf_middle,
          amount: Number(r.amount),
        }, r.origin_phone ?? null);
        if (m) {
          await context.supabase.from("pix_payment_proofs")
            .update({ match_level: m.level, linked_kind: m.candidate.kind, linked_id: m.candidate.id })
            .eq("id", r.id);
          // Auto-baixa em qualquer nível de match — bank_reconciled continua false.
          await applyConfirmation(context.supabase, tc.id, context.userId, r.id, m.candidate.kind, m.candidate.id);
          (r as any).match_level = m.level;
          (r as any).status = "confirmado";
          (r as any).linked_kind = m.candidate.kind;
          (r as any).linked_id = m.candidate.id;
        }
      }
    }

    // Filtrar linhas que mudaram de status por auto-baixa (ex.: pediu "aguardando" mas re-match confirmou).
    const filtered = (rows ?? []).filter((r: any) =>
      data.status === "all" ? true : r.status === data.status,
    );

    // Resolver o "match feito" para cada linha confirmada — mostrar aluno/cobrança/item vinculado.
    const chargeIds = filtered.filter((r: any) => r.linked_kind === "charge").map((r: any) => r.linked_id).filter(Boolean);
    const uniIds    = filtered.filter((r: any) => r.linked_kind === "uniform").map((r: any) => r.linked_id).filter(Boolean);
    const prodIds   = filtered.filter((r: any) => r.linked_kind === "product").map((r: any) => r.linked_id).filter(Boolean);
    const chargeMap = new Map<string, { student_name: string; label: string }>();
    if (chargeIds.length) {
      const { data: ch } = await context.supabase
        .from("pix_payment_proofs")
        .select("id, linked_id")
        .in("id", chargeIds);
      const stuIds = Array.from(new Set((ch ?? []).map((r: any) => r.linked_id).filter(Boolean)));
      const stuInfo = new Map<string, { name: string; plan_type: string | null }>();
      if (stuIds.length) {
        const { data: srows } = await context.supabase
          .from("students").select("id, name, plan_prices(plan_type)").in("id", stuIds);
        for (const s of (srows ?? [])) {
          stuInfo.set((s as any).id, {
            name: (s as any).name ?? "",
            plan_type: (s as any).plan_prices?.plan_type ?? null,
          });
        }
      }
      for (const c of (ch ?? [])) {
        const info = stuInfo.get((c as any).linked_id);
        chargeMap.set((c as any).id, {
          student_name: info?.name ?? "",
          label: `Cobrança ${info?.plan_type ?? "Mensalidade"}`,
        });
      }
    }
    const uniMap = new Map<string, { student_name: string; label: string }>();
    if (uniIds.length) {
      const { data: u } = await context.supabase
        .from("uniform_orders").select("id, students(name), uniforms(name)").in("id", uniIds);
      for (const r of (u ?? [])) uniMap.set((r as any).id, {
        student_name: (r as any).students?.name ?? "",
        label: (r as any).uniforms?.name ?? "Uniforme",
      });
    }
    const prodMap = new Map<string, { student_name: string; label: string }>();
    if (prodIds.length) {
      const { data: p } = await context.supabase
        .from("product_orders").select("id, students(name), products(name)").in("id", prodIds);
      for (const r of (p ?? [])) prodMap.set((r as any).id, {
        student_name: (r as any).students?.name ?? "",
        label: (r as any).products?.name ?? "Mercadoria",
      });
    }

    // signed urls for images + link info
    const items = await Promise.all(filtered.map(async (r: any) => {
      let signed: string | null = null;
      if (r.image_url) {
        const { data: s } = await context.supabase.storage
          .from("receipts").createSignedUrl(r.image_url, 60 * 60);
        signed = s?.signedUrl ?? null;
      }
      let linked_student_name: string | null = null;
      let linked_label: string | null = null;
      if (r.linked_kind === "charge" && r.linked_id) {
        linked_student_name = chargeMap.get(r.linked_id)?.student_name ?? null;
        linked_label = chargeMap.get(r.linked_id)?.label ?? null;
      } else if (r.linked_kind === "uniform" && r.linked_id) {
        linked_student_name = uniMap.get(r.linked_id)?.student_name ?? null;
        linked_label = uniMap.get(r.linked_id)?.label ?? null;
      } else if (r.linked_kind === "product" && r.linked_id) {
        linked_student_name = prodMap.get(r.linked_id)?.student_name ?? null;
        linked_label = prodMap.get(r.linked_id)?.label ?? null;
      }
      return { ...r, image_signed_url: signed, linked_student_name, linked_label };
    }));
    return { items };
  });

// ============ Pending orders awaiting PIX confirmation ============

export const listAwaitingPixOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    // Reconcilia: garante que TODO contrato Pix (comprovante, presencial ou
    // online) tenha uma linha 'aguardando' em pix_payment_proofs. Assim o
    // dropdown de "Vincular a uma cobrança pendente" enxerga também os pix
    // presenciais/online — Mercado Livre/Pago fica fora porque não é pix_*.
    const { data: eligibleStudents } = await context.supabase
      .from("students")
      .select("id, name, payment_method, plan_discount, plan_price_id, plan_prices(price), parents(name, phone, cpf)")
      .eq("training_center_id", tc.id)
      .not("plan_price_id", "is", null);
    const pixStudents = (eligibleStudents ?? []).filter((s: any) => {
      const m = String(s.payment_method || "");
      return m === "pix_comprovante" || m === "pix_presencial" || m === "pix";
    });
    if (pixStudents.length) {
      const cutoff = new Date(Date.now() - 25 * 24 * 3600 * 1000).toISOString();
      const { data: existing } = await context.supabase
        .from("pix_payment_proofs")
        .select("linked_id, status, created_at")
        .eq("training_center_id", tc.id)
        .eq("source", "contract")
        .eq("linked_kind", "student")
        .or(`status.eq.aguardando,and(status.eq.confirmado,created_at.gte.${cutoff})`);
      const withCharge = new Set((existing ?? []).map((r: any) => r.linked_id));
      const missing = pixStudents.filter((s: any) => !withCharge.has(s.id));
      if (missing.length) {
        const rows = missing.map((s: any) => {
          const price = Number(s.plan_prices?.price ?? 0);
          const amount = Math.max(0, price - Number(s.plan_discount ?? 0));
          if (amount <= 0) return null;
          const rel: any = s.parents;
          const parent: any = Array.isArray(rel) ? (rel[0] ?? null) : (rel ?? null);
          const cpfDigits = (parent?.cpf || "").replace(/\D/g, "");
          return {
            training_center_id: tc.id,
            status: "aguardando",
            source: "contract",
            amount,
            payer_name: parent?.name ?? s.name ?? null,
            payer_cpf_middle: cpfDigits ? cpfDigits.slice(3, 9) : null,
            origin_phone: parent?.phone ?? null,
            match_level: "none",
            linked_kind: "student",
            linked_id: s.id,
            note: `Cobrança pendente — ${s.payment_method}`,
          };
        }).filter((r): r is NonNullable<typeof r> => r !== null);
        if (rows.length) await context.supabase.from("pix_payment_proofs").insert(rows);
      }
    }

    // Espelha a aba Cobranças filtrando por Pix (Enviar comprovante):
    //   1) Cobranças de contrato (pix_payment_proofs source='contract') aguardando.
    //   2) Vendas de uniforme com payment_method='pix_comprovante' não pagas.
    //   3) Vendas de mercadoria com payment_method='pix_comprovante' não pagas.
    const [chRes, uRes, pRes] = await Promise.all([

      context.supabase
        .from("pix_payment_proofs")
        .select("id, amount, created_at, linked_id")
        .eq("training_center_id", tc.id)
        .eq("source", "contract")
        .eq("linked_kind", "student")
        .eq("status", "aguardando")
        .order("created_at", { ascending: false })
        .limit(200),
      context.supabase
        .from("uniform_orders")
        .select("id, amount, discount, created_at, size, shirt_name, shirt_number, buyer_name, uniforms(name), students(id, name, parents(name, cpf, phone))")
        .eq("training_center_id", tc.id)
        .eq("payment_method", "pix_comprovante")
        .not("status", "eq", "pago")
        .not("status", "eq", "cancelado")
        .order("created_at", { ascending: false })
        .limit(200),
      context.supabase
        .from("product_orders")
        .select("id, amount, discount, quantity, created_at, buyer_name, products(name), students(id, name, parents(name, cpf, phone))")
        .eq("training_center_id", tc.id)
        .eq("payment_method", "pix_comprovante")
        .not("status", "eq", "pago")
        .not("status", "eq", "cancelado")
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    if (chRes.error) throw chRes.error;
    const chData = chRes.data ?? [];

    const studentIds = Array.from(new Set(chData.map((r: any) => r.linked_id).filter(Boolean)));
    const stuMap = new Map<string, any>();
    if (studentIds.length) {
      const { data: srows } = await context.supabase
        .from("students")
        .select("id, name, plan_prices(plan_type), parents(name, cpf, phone)")
        .in("id", studentIds);
      for (const s of (srows ?? [])) stuMap.set((s as any).id, s);
    }
    const items: any[] = chData.map((r: any) => {
      const s: any = stuMap.get(r.linked_id);
      const rel = s?.parents;
      const parent = Array.isArray(rel) ? rel[0] : rel;
      const planType = s?.plan_prices?.plan_type ?? "Mensalidade";
      return {
        kind: "charge" as const, id: r.id,
        amount: Math.max(0, Number(r.amount ?? 0)),
        created_at: r.created_at,
        item_name: `Cobrança ${planType}`,
        student_name: s?.name ?? "",
        parent_name: parent?.name ?? "",
        parent_phone: parent?.phone ?? "",
        parent_cpf: parent?.cpf ?? "",
      };
    });

    for (const o of (uRes.data ?? []) as any[]) {
      const parent = Array.isArray(o.students?.parents) ? o.students.parents[0] : o.students?.parents;
      const net = Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0));
      items.push({
        kind: "uniform" as const, id: o.id,
        amount: net,
        created_at: o.created_at,
        item_name: `${o.uniforms?.name ?? "Uniforme"}${o.size ? " — Tam. " + o.size : ""}${o.shirt_number ? " · Nº " + o.shirt_number : ""}${o.shirt_name ? " · " + o.shirt_name : ""}`,
        student_name: o.students?.name ?? o.buyer_name ?? "Avulso",
        parent_name: parent?.name ?? "",
        parent_phone: parent?.phone ?? "",
        parent_cpf: parent?.cpf ?? "",
      });
    }
    for (const o of (pRes.data ?? []) as any[]) {
      const parent = Array.isArray(o.students?.parents) ? o.students.parents[0] : o.students?.parents;
      const net = Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0));
      items.push({
        kind: "product" as const, id: o.id,
        amount: net,
        created_at: o.created_at,
        item_name: `${o.products?.name ?? "Mercadoria"}${o.quantity > 1 ? " · " + o.quantity + "×" : ""}`,
        student_name: o.students?.name ?? o.buyer_name ?? "Avulso",
        parent_name: parent?.name ?? "",
        parent_phone: parent?.phone ?? "",
        parent_cpf: parent?.cpf ?? "",
      });
    }

    // 4) Taxas de evento (source='fee') cuja forma efetiva é Pix (enviar comprovante).
    const { data: feeProofs } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, created_at, linked_id, note, payment_method")
      .eq("training_center_id", tc.id)
      .eq("source", "fee")
      .eq("linked_kind", "student")
      .eq("status", "aguardando")
      .order("created_at", { ascending: false })
      .limit(200);
    if ((feeProofs ?? []).length) {
      const { data: fees } = await context.supabase
        .from("fees")
        .select("id, name, payment_method, championships(name)")
        .eq("training_center_id", tc.id);
      const feeById = new Map((fees ?? []).map((f: any) => [f.id, f]));
      const eligible = (feeProofs ?? []).filter((r: any) => {
        const feeId = String(r.note ?? "").match(/\[fee:([^\]]+)\]/)?.[1];
        const fee = feeId ? feeById.get(feeId) : null;
        const method = r.payment_method ?? fee?.payment_method ?? null;
        return method === "pix_comprovante";
      });
      const feeStudentIds = Array.from(new Set(eligible.map((r: any) => r.linked_id).filter(Boolean)));
      const feeStuMap = new Map<string, any>();
      if (feeStudentIds.length) {
        const { data: srows } = await context.supabase
          .from("students")
          .select("id, name, parents(name, cpf, phone)")
          .in("id", feeStudentIds);
        for (const s of (srows ?? [])) feeStuMap.set((s as any).id, s);
      }
      for (const r of eligible as any[]) {
        const feeId = String(r.note ?? "").match(/\[fee:([^\]]+)\]/)?.[1];
        const fee: any = feeId ? feeById.get(feeId) : null;
        const s: any = feeStuMap.get(r.linked_id);
        const rel = s?.parents;
        const parent = Array.isArray(rel) ? rel[0] : rel;
        items.push({
          kind: "charge" as const,
          id: r.id,
          amount: Math.max(0, Number(r.amount ?? 0)),
          created_at: r.created_at,
          item_name: `Taxa ${fee?.name ?? ""}${fee?.championships?.name ? ` — ${fee.championships.name}` : ""}`.trim(),
          student_name: s?.name ?? "",
          parent_name: parent?.name ?? "",
          parent_phone: parent?.phone ?? "",
          parent_cpf: parent?.cpf ?? "",
        });
      }
    }

    items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return { items };
  });




// ============ OCR + Upload proof (delegates to server-only processor) ============

export const uploadPixProof = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      image_base64: z.string().min(20),
      mime: z.string().default("image/jpeg"),
      origin_phone: z.string().nullable().optional(),
      source: z.enum(["manual", "whatsapp"]).default("manual"),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { processIncomingPixImage } = await import("@/lib/pix-proofs/processor.server");
    return await processIncomingPixImage({
      supabase: context.supabase,
      tcId: tc.id,
      userId: context.userId,
      base64: data.image_base64,
      mime: data.mime,
      originPhone: data.origin_phone ?? null,
      source: data.source,
    });
  });


// ============ Manual decide ============

export const decidePixProof = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      id: z.string().uuid(),
      decision: z.enum(["confirm", "reject"]),
      link_kind: z.enum(["uniform", "product", "charge"]).nullable().optional(),
      link_id: z.string().uuid().nullable().optional(),
      note: z.string().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { data: proof, error } = await context.supabase
      .from("pix_payment_proofs").select("*").eq("id", data.id).single();
    if (error) throw error;
    if (proof.training_center_id !== tc.id) throw new Error("Sem permissão");

    if (data.decision === "reject") {
      await context.supabase.from("pix_payment_proofs").update({
        status: "rejeitado", reviewed_at: new Date().toISOString(),
        reviewed_by: context.userId, note: data.note ?? null,
      }).eq("id", data.id);
      return { ok: true };
    }
    if (!data.link_kind || !data.link_id) {
      throw new Error("Vincule a uma cobrança pendente para confirmar.");
    }
    const { applyConfirmation, notifyMatched } = await import("@/lib/pix-proofs/processor.server");
    await applyConfirmation(context.supabase, tc.id, context.userId, data.id, data.link_kind, data.link_id);
    if (proof.origin_phone) {
      await notifyMatched(
        context.supabase, tc.id, proof.origin_phone,
        { amount: proof.amount, pix_date: proof.pix_date },
        { kind: data.link_kind, id: data.link_id } as any,
      ).catch(() => {});
    }
    return { ok: true };
  });

// ============ Pagadores bloqueados ============

export const listBlockedPayers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("blocked_payers")
      .select("id, name, cpf, note, created_at")
      .eq("training_center_id", tc.id)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return { items: data ?? [] };
  });

export const saveBlockedPayer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      id: z.string().uuid().nullable().optional(),
      name: z.string().trim().min(3, "Informe o nome do pagador").max(160),
      cpf: z.string().trim().min(11, "Informe o CPF/CNPJ").max(20),
      note: z.string().trim().max(300).nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const digits = onlyDigits(data.cpf);
    if (digits.length !== 11 && digits.length !== 14) {
      throw new Error("CPF deve ter 11 dígitos ou CNPJ 14 dígitos.");
    }
    const payload = {
      training_center_id: tc.id,
      name: data.name,
      cpf: digits,
      note: data.note || null,
    };
    if (data.id) {
      const { error } = await context.supabase
        .from("blocked_payers").update(payload).eq("id", data.id).eq("training_center_id", tc.id);
      if (error) throw error;
      return { id: data.id };
    }
    const { data: ins, error } = await context.supabase
      .from("blocked_payers").insert(payload).select("id").single();
    if (error) throw error;
    return { id: ins.id };
  });

export const deleteBlockedPayer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("blocked_payers").delete().eq("id", data.id).eq("training_center_id", tc.id);
    if (error) throw error;
    return { ok: true };
  });


// ============ Excluir proof ============

export const deletePixProof = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { data: p } = await context.supabase
      .from("pix_payment_proofs").select("id, image_url, training_center_id, linked_id, linked_kind, status, source")
      .eq("id", data.id).maybeSingle();
    if (!p) return { ok: true, skipped: true, reason: "not_found" };
    if (p.training_center_id !== tc.id) throw new Error("Sem permissão");
    // Cobranças de contrato (source='contract') só são protegidas enquanto aguardando.
    // Quando rejeitadas, podem ser excluídas definitivamente desta tela.
    if (p.source === "contract" && p.status === "aguardando") {
      return { ok: false, skipped: true, reason: "contract_charge", message: "Cobrança de contrato ignorada — exclua em Cobrança." };
    }

    // Se já confirmado, reverter primeiro (remove entrada no caixa e reabre o pedido vinculado).
    if (p.status === "confirmado" && p.linked_kind && p.linked_id) {
      if (p.linked_kind === "charge") {
        const { data: ch } = await context.supabase
          .from("pix_payment_proofs").select("linked_id").eq("id", p.linked_id).maybeSingle();
        await context.supabase.from("pix_payment_proofs").update({
          status: "aguardando", reviewed_at: null, reviewed_by: null, note: null,
        }).eq("id", p.linked_id);
        await context.supabase.from("cash_movements")
          .delete().eq("source_table", "pix_payment_proofs").eq("source_id", p.id);
        if ((ch as any)?.linked_id) {
          await context.supabase.from("students")
            .update({ payment_status: "pendente" }).eq("id", (ch as any).linked_id);
        }
      } else {
        const table = p.linked_kind === "uniform" ? "uniform_orders" : "product_orders";
        await context.supabase.from(table).update({
          status: "aguardando_confirmacao", confirmed_at: null, pix_proof_id: null,
        }).eq("id", p.linked_id);
        await context.supabase.from("cash_movements")
          .delete().eq("source_table", "pix_payment_proofs").eq("source_id", p.id);
      }
    }

    if (p.image_url) await context.supabase.storage.from("receipts").remove([p.image_url]);
    await context.supabase.from("pix_payment_proofs").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ Conciliação Bancária ============

export const setBankReconciled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      id: z.string().uuid(),
      reconciled: z.boolean(),
      verified_deposit_date: z.string().nullable().optional(),
      verified_depositor_name: z.string().nullable().optional(),
      verified_amount: z.coerce.number().nullable().optional(),
      verified_by_name: z.string().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    let verifiedByName = data.verified_by_name ?? null;
    if (data.reconciled && !verifiedByName) {
      const { data: prof } = await context.supabase
        .from("profiles").select("name, email").eq("id", context.userId).maybeSingle();
      verifiedByName = (prof as any)?.name || (prof as any)?.email || null;
    }
    const patch: any = {
      bank_reconciled: data.reconciled,
      bank_reconciled_at: data.reconciled ? new Date().toISOString() : null,
    };
    if (data.reconciled) {
      patch.verified_by = context.userId;
      patch.verified_by_name = verifiedByName;
      if (data.verified_deposit_date !== undefined) patch.verified_deposit_date = data.verified_deposit_date;
      if (data.verified_depositor_name !== undefined) patch.verified_depositor_name = data.verified_depositor_name;
      if (data.verified_amount !== undefined && data.verified_amount !== null) patch.verified_amount = data.verified_amount;
    } else {
      patch.verified_by = null;
      patch.verified_by_name = null;
      patch.verified_deposit_date = null;
      patch.verified_depositor_name = null;
      patch.verified_amount = null;
    }
    const { error } = await context.supabase.from("pix_payment_proofs").update(patch)
      .eq("id", data.id).eq("training_center_id", tc.id);
    if (error) throw error;
    return { ok: true };
  });

export const revertPixProof = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { data: p } = await context.supabase
      .from("pix_payment_proofs").select("*").eq("id", data.id).single();
    if (!p || p.training_center_id !== tc.id) throw new Error("Sem permissão");
    if (p.linked_kind && p.linked_id) {
      if (p.linked_kind === "charge") {
        const { data: ch } = await context.supabase
          .from("pix_payment_proofs").select("linked_id").eq("id", p.linked_id).maybeSingle();
        // Re-open the contract charge and remove the cash movement we created.
        await context.supabase.from("pix_payment_proofs").update({
          status: "aguardando", reviewed_at: null, reviewed_by: null, note: null,
        }).eq("id", p.linked_id);
        await context.supabase.from("cash_movements")
          .delete().eq("source_table", "pix_payment_proofs").eq("source_id", p.id);
        if ((ch as any)?.linked_id) {
          await context.supabase.from("students")
            .update({ payment_status: "pendente" }).eq("id", (ch as any).linked_id);
        }
      } else {
        const table = p.linked_kind === "uniform" ? "uniform_orders" : "product_orders";
        await context.supabase.from(table).update({
          status: "aguardando_confirmacao", confirmed_at: null, pix_proof_id: null,
        }).eq("id", p.linked_id);
        await context.supabase.from("cash_movements")
          .delete().eq("source_table", "pix_payment_proofs").eq("source_id", p.id);
      }
    }

    await context.supabase.from("pix_payment_proofs").update({
      status: "aguardando", reviewed_at: null, reviewed_by: null,
      linked_kind: null, linked_id: null, bank_reconciled: false, bank_reconciled_at: null,
    }).eq("id", data.id);
    return { ok: true };
  });

// CSV reconciliation: rows = [{ date: 'YYYY-MM-DD'|null, amount: number, payer?: string }]
export const reconcileWithBankCsv = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      rows: z.array(z.object({
        date: z.string().nullable().optional(),
        amount: z.number(),
        payer: z.string().nullable().optional(),
      })).min(1),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const tc = await getMyTc(context.supabase, context.userId);
    const { data: proofs, error } = await context.supabase
      .from("pix_payment_proofs").select("id, amount, pix_date, bank_reconciled")
      .eq("training_center_id", tc.id).eq("status", "confirmado");
    if (error) throw error;
    let matched = 0;
    for (const proof of proofs ?? []) {
      if (proof.bank_reconciled) continue;
      const hit = data.rows.find((r) =>
        Math.abs(Number(r.amount) - Number(proof.amount ?? 0)) < 0.01 &&
        (!r.date || !proof.pix_date || r.date === proof.pix_date),
      );
      if (hit) {
        await context.supabase.from("pix_payment_proofs").update({
          bank_reconciled: true, bank_reconciled_at: new Date().toISOString(),
        }).eq("id", proof.id);
        matched++;
      }
    }
    return { matched, total: (proofs ?? []).length };
  });
